/**
 * Unified Haptic Feedback Hook
 * Automatically detects native platform (iOS/Android) or web and provides appropriate haptic feedback
 */

import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { useCallback } from 'react';

export type HapticStyle = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection';

export const useHaptics = () => {
  const isNative = Capacitor.isNativePlatform();
  const isWebSupported = typeof navigator !== 'undefined' && 'vibrate' in navigator;

  const triggerHaptic = useCallback(async (style: HapticStyle = 'light') => {
    try {
      if (isNative) {
        // Native iOS/Android using Capacitor Haptics
        switch (style) {
          case 'light':
            await Haptics.impact({ style: ImpactStyle.Light });
            break;
          case 'medium':
            await Haptics.impact({ style: ImpactStyle.Medium });
            break;
          case 'heavy':
            await Haptics.impact({ style: ImpactStyle.Heavy });
            break;
          case 'success':
            await Haptics.notification({ type: NotificationType.Success });
            break;
          case 'warning':
            await Haptics.notification({ type: NotificationType.Warning });
            break;
          case 'error':
            await Haptics.notification({ type: NotificationType.Error });
            break;
          case 'selection':
            await Haptics.selectionStart();
            break;
        }
      } else if (isWebSupported) {
        // Web fallback using Vibration API
        const patterns: Record<HapticStyle, number | number[]> = {
          light: 10,
          medium: 20,
          heavy: 40,
          success: [10, 50, 10],
          warning: [20, 100, 20],
          error: [40, 100, 40, 100, 40],
          selection: 5,
        };
        
        const pattern = patterns[style];
        navigator.vibrate(pattern);
      }
    } catch (error) {
      // Fail silently - haptics should never break the app
      if (import.meta.env.DEV) {
        console.warn('Haptics error:', error);
      }
    }
  }, [isNative, isWebSupported]);

  return {
    light: useCallback(() => triggerHaptic('light'), [triggerHaptic]),
    medium: useCallback(() => triggerHaptic('medium'), [triggerHaptic]),
    heavy: useCallback(() => triggerHaptic('heavy'), [triggerHaptic]),
    success: useCallback(() => triggerHaptic('success'), [triggerHaptic]),
    warning: useCallback(() => triggerHaptic('warning'), [triggerHaptic]),
    error: useCallback(() => triggerHaptic('error'), [triggerHaptic]),
    selection: useCallback(() => triggerHaptic('selection'), [triggerHaptic]),
    trigger: triggerHaptic,
    isSupported: isNative || isWebSupported,
  };
};
